import { Box, Stack, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import { RootState } from 'store'
import {
  useAddInvoiceEventMutation,
  useGetInvoiceByIdQuery,
  useGetInvoiceEmailsQuery,
} from 'store/invoices/invoices.api'

import TranspotsLoadingButton from 'components/transpots-loading-button/transpots-loading-button.component'
import { useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { InvoiceEvent } from 'store/invoices/invoices.types'
import { convertTimestampToDateAndTime } from 'pages/invoice/invoice.utils'

const InvoiceDialogTimelineInvoice = () => {
  const [eftLoading, setEFTLoading] = useState(false)
  const [chequeLoading, setChequeLoading] = useState(false)

  const invoiceId = useSelector(
    (state: RootState) => state.invoices.selectedInvoiceId
  )

  const { data: invoice, refetch } = useGetInvoiceByIdQuery({
    id: invoiceId ?? skipToken,
  })

  const { data: invoiceEmails } = useGetInvoiceEmailsQuery(
    invoiceId ? { invoiceId } : skipToken
  )

  const [addEvent, { isError: isAddEventError }] = useAddInvoiceEventMutation()

  const sentEvent = async (event: 'SentEFT' | 'SentCheque') => {
    switch (event) {
      case 'SentEFT':
        setEFTLoading(true)
        setChequeLoading(false)
        break
      case 'SentCheque':
        setEFTLoading(false)
        setChequeLoading(true)
        break
      default:
        setEFTLoading(false)
        setChequeLoading(false)
        break
    }

    await addEvent({ event, id: invoiceId, notes: event })

    if (!isAddEventError) {
      refetch()
      setEFTLoading(false)
      setChequeLoading(false)
    }
  }

  const hasSentEFT = invoice?.events.some(
    (item) => item.event === InvoiceEvent.SentEFT
  )
  const hasSentCheque = invoice?.events.some(
    (item) => item.event === InvoiceEvent.SentCheque
  )

  // Find the InvoiceSent event to get the date/time
  const invoiceSentEvent = invoice?.events.find(
    (item) => item.event === InvoiceEvent.InvoiceSent
  )
  const invoiceSentDate = invoiceSentEvent
    ? convertTimestampToDateAndTime(invoiceSentEvent.createdDate)
    : null

  // Extract all unique email addresses from invoice emails
  // Multiple emails can be in one recipient string separated by commas
  const allRecipients = invoiceEmails
    ? invoiceEmails
        .flatMap((email) =>
          email.recipient
            .split(',')
            .map((recipient) => recipient.trim())
            .filter((recipient) => recipient.length > 0)
        )
        .filter((email, index, self) => self.indexOf(email) === index) // Remove duplicates
    : []

  return (
    <Box px={2} py={4}>
      {/* Invoice Sent */}
      {invoiceSentDate && (
        <Stack>
          <Typography fontWeight={700}>Invoice Sent</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>{invoiceSentDate.date}</Typography>
            <Typography fontWeight={700}>{invoiceSentDate.time}</Typography>
          </Stack>
        </Stack>
      )}
      {/* Sent To Addresses */}
      {allRecipients.length > 0 && (
        <Stack mt={4}>
          <Typography>Sent To:</Typography>
          {allRecipients.map((email, index) => (
            <Typography key={index} sx={{ mt: 1 }}>
              {email}
            </Typography>
          ))}
        </Stack>
      )}
      {/* Buttons */}
      <Stack justifyContent="center" alignItems="center" mt={6} spacing={2}>
        <TranspotsLoadingButton
          variant="contained"
          handleClick={() => sentEvent('SentEFT')}
          label="Sent EFT"
          loading={eftLoading}
          loadingText="Sending EFT..."
          disabled={hasSentEFT || hasSentCheque}
        />
        <TranspotsLoadingButton
          variant="contained"
          handleClick={() => sentEvent('SentCheque')}
          label="Sent Cheque"
          loading={chequeLoading}
          loadingText="Sending Cheque..."
          disabled={hasSentEFT || hasSentCheque}
        />
      </Stack>
    </Box>
  )
}

export default InvoiceDialogTimelineInvoice
